/**
 * One session setting, edited at one level of the chain (workflow, cycle,
 * track or session; ruling R19). The most specific level wins and an unset
 * level inherits from the levels above it, which this row names. Stored in
 * session_policies at the given scope; the server reads the same rows
 * (internal_session_policy_value).
 *
 * One component for every setting and every level, so the places cannot
 * drift apart: "Change ranking order" (S-33), what the room's timer does at
 * zero (S-2) and how names are shown (S-3).
 */
import { useEffect, useMemo, useState } from 'react';
import {
  useSessionPolicyStore,
  type SessionPolicy,
  type SessionPolicyScopeKind,
  type UpsertSessionPolicyInput,
} from '@/stores/prioritization/sessionPolicyStore';
import { resolveSessionPolicy, type SessionPolicySource } from '@/lib/effectiveConfigResolver';
import { Select } from '@/components/ds';

export interface SessionPolicyScopeIds {
  workspaceId?: string | null;
  workflowId?: string | null;
  cycleId?: string | null;
  trackId?: string | null;
  sessionId?: string | null;
}

/** The settings this row can edit, by their field on the policy row. */
export type SessionPolicyField = 'allowRankOrderChange' | 'timerAtZero' | 'anonymity';

export interface SessionPolicyChoice<T> {
  /** The value saved. */
  value: T;
  /** What the person reads. */
  label: string;
}

export interface SessionPolicySettingProps<T extends string | boolean> {
  field: SessionPolicyField;
  label: string;
  ariaLabel: string;
  choices: Array<SessionPolicyChoice<T>>;
  /** The value when no level sets it. */
  fallback: T;
  /** One sentence on what the effective value means. */
  describe: (effective: T) => string;
  /** The level this row edits. */
  scopeKind: Extract<SessionPolicyScopeKind, 'workflow' | 'cycle' | 'track' | 'session'>;
  scopeId: string;
  /** Ids of this level and the levels above it, for the inherited value. */
  ids: SessionPolicyScopeIds;
  /** The person may change this level's settings. Otherwise the value is shown, not the control. */
  canEdit: boolean;
  /** Called after a save, so a screen that reads the server's rule can re-read it. */
  onSaved?: () => void;
  testId?: string;
}

const SESSION_POLICY_SOURCE_WORDS: Record<SessionPolicySource, string> = {
  session: 'this session',
  track: 'the track',
  cycle: 'the cycle',
  workflow: 'the workflow',
  workspace: 'the workspace',
  default: 'the default',
};

const keyOf = (v: string | boolean) => String(v);

export function SessionPolicySetting<T extends string | boolean>({
  field,
  label,
  ariaLabel,
  choices,
  fallback,
  describe,
  scopeKind,
  scopeId,
  ids,
  canEdit,
  onSaved,
  testId,
}: SessionPolicySettingProps<T>) {
  const policies = useSessionPolicyStore((s) => s.policies);
  const fetchPoliciesForScopes = useSessionPolicyStore((s) => s.fetchPoliciesForScopes);
  const upsertPolicy = useSessionPolicyStore((s) => s.upsertPolicy);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load the rows this row needs (entity stores load cycle/track rows already;
  // workflow and session rows are fetched here).
  useEffect(() => {
    const wanted: Array<[SessionPolicyScopeKind, string | null | undefined]> = [
      ['workspace', ids.workspaceId],
      ['workflow', ids.workflowId],
      ['cycle', ids.cycleId],
      ['track', ids.trackId],
      ['session', ids.sessionId],
    ];
    for (const [kind, id] of wanted) {
      if (id && !useSessionPolicyStore.getState().policies[`${kind}:${id}`]) {
        void fetchPoliciesForScopes(kind, [id]);
      }
    }
  }, [ids.workspaceId, ids.workflowId, ids.cycleId, ids.trackId, ids.sessionId, fetchPoliciesForScopes]);

  const valueAt = (kind: SessionPolicyScopeKind, id: string | null | undefined): T | null => {
    if (!id) return null;
    const v = (policies[`${kind}:${id}`] as SessionPolicy | undefined)?.[field];
    return v === null || v === undefined ? null : (v as T);
  };

  const own = valueAt(scopeKind, scopeId);
  // What this level inherits: the levels above it only.
  const inherited = useMemo(() => {
    const above = {
      workspace: valueAt('workspace', ids.workspaceId),
      workflow: scopeKind === 'workflow' ? null : valueAt('workflow', ids.workflowId),
      cycle: scopeKind === 'workflow' || scopeKind === 'cycle' ? null : valueAt('cycle', ids.cycleId),
      track: scopeKind === 'session' ? valueAt('track', ids.trackId) : null,
    };
    return resolveSessionPolicy<T>(above, fallback);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policies, field, fallback, scopeKind, ids.workspaceId, ids.workflowId, ids.cycleId, ids.trackId]);

  const effective = own ?? inherited.value;
  const labelOf = (v: T) => choices.find((c) => c.value === v)?.label ?? String(v);
  const inheritLabel = `Inherit (${labelOf(inherited.value)}, from ${SESSION_POLICY_SOURCE_WORDS[inherited.source]})`;

  const save = async (next: string) => {
    setBusy(true);
    setError(null);
    const chosen = next === '' ? null : choices.find((c) => keyOf(c.value) === next)?.value ?? null;
    const saved = await upsertPolicy({
      scopeKind,
      scopeId,
      [field]: chosen,
    } as UpsertSessionPolicyInput);
    if (!saved) setError(useSessionPolicyStore.getState().error ?? 'The setting could not be saved.');
    else onSaved?.();
    setBusy(false);
  };

  return (
    <div className="px-4 py-2.5 flex flex-wrap items-center gap-4" data-testid={testId}>
      <span className="text-sm font-medium text-[var(--text-sec)] w-28 shrink-0">{label}</span>
      {canEdit ? (
        <Select
          size="sm"
          value={own === null ? '' : keyOf(own)}
          onChange={(e) => void save(e.target.value)}
          disabled={busy}
          aria-label={ariaLabel}
          className="min-w-[140px]"
          options={[
            { value: '', label: inheritLabel },
            ...choices.map((c) => ({ value: keyOf(c.value), label: c.label })),
          ]}
        />
      ) : (
        <span className="text-sm text-[var(--text-pri)]">
          {labelOf(effective)}
          {own === null && (
            <span className="text-xs text-[var(--text-ter)]"> (from {SESSION_POLICY_SOURCE_WORDS[inherited.source]})</span>
          )}
        </span>
      )}
      <span className="text-xs text-[var(--text-ter)]">{describe(effective)}</span>
      {error && <span role="alert" className="text-xs text-[var(--coral)]">{error}</span>}
    </div>
  );
}
